import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../../lib/api";

import { Plus, Edit3, Trash2, ExternalLink, Star, FileUp, X } from "lucide-react";

const CATEGORY_LABEL = {
  "findings":     "Finding",
  "teardowns":    "Teardown",
  "reports":      "Report",
  "case-studies": "Case Study",
  "methodology":  "Methodology",
  "resources":    "Resource",
};
const STATUS_LABEL = { draft: "Draft", published: "Published", scheduled: "Scheduled" };

const SAMPLE_MD = `---
title: How AI Misclassifies Commercial Contractors
category: findings
industry: Construction
tags: [construction, evidence, misclassification]
platforms_tested: [ChatGPT, Claude, Gemini, Perplexity]
featured: false
status: draft
finding_summary: 73% of commercial GC entities are routed to residential by AI.
business_implication: Commercial buyers never see these firms in AI-led discovery.
---

# How AI Misclassifies Commercial Contractors

Across **48 audits** of mid-sized commercial general contractors,
AI platforms misclassified the entity as a residential construction
company in 73% of category-discovery queries.

## Key findings

- ChatGPT misclassified in 31 of 48 audits
- Claude misclassified in 28 of 48 audits
- Schema markup absence was the strongest correlate

## What this means

Buyers searching for commercial GCs in AI-led discovery never encounter
these firms. The entire top-of-funnel evaporates.
`;

function ImportModal({ onClose, onCreated }) {
  const [markdown, setMarkdown] = useState("");
  const [defaultCategory, setDefaultCategory] = useState("findings");
  const [defaultStatus, setDefaultStatus] = useState("draft");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(md|markdown|txt)$/i.test(f.name)) {
      setErr("Please upload a .md, .markdown or .txt file.");
      return;
    }
    if (f.size > 1.5 * 1024 * 1024) {
      setErr("File too large (>1.5 MB). Paste the content instead.");
      return;
    }
    setErr("");
    setMarkdown(await f.text());
  };

  const onSubmit = async () => {
    if (!markdown.trim()) {
      setErr("Paste markdown content or upload a .md file first.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/publications/import", {
        markdown,
        default_category: defaultCategory,
        default_status: defaultStatus,
      });
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
         data-testid="admin-pub-import-modal">
      <div className="bg-[#0c0a08] border border-[#2a2620] rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2620]">
          <div>
            <h2 className="font-serif-display text-xl text-[#f4efe6]">Import publication from Markdown</h2>
            <p className="text-xs text-[#8a7e6d] mt-1">
              Upload a <code className="font-mono-eav">.md</code> file or paste markdown. YAML front-matter is parsed automatically.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-[#8a7e6d] hover:text-[#f4efe6]"
                  data-testid="admin-pub-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".md,.markdown,.txt" className="hidden"
                   onChange={onFile} data-testid="admin-pub-import-file" />
            <button onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-[#b8963e] text-[#b8963e] hover:bg-[#b8963e]/10 rounded-sm text-sm font-mono-eav tracking-wider uppercase"
                    data-testid="admin-pub-import-upload-btn">
              <FileUp className="w-4 h-4" /> Choose file
            </button>
            <button onClick={() => setMarkdown(SAMPLE_MD)}
                    className="text-xs text-[#8a7e6d] hover:text-[#b8963e] underline underline-offset-2"
                    data-testid="admin-pub-import-sample">
              Load sample template
            </button>
            <div className="flex items-center gap-2 ml-auto text-xs text-[#8a7e6d]">
              <label>
                Default category:{" "}
                <select value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)}
                        className="eav-input py-1 px-2 ml-1"
                        data-testid="admin-pub-import-default-category">
                  {Object.entries(CATEGORY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="ml-3">
                Status:{" "}
                <select value={defaultStatus} onChange={(e) => setDefaultStatus(e.target.value)}
                        className="eav-input py-1 px-2 ml-1"
                        data-testid="admin-pub-import-default-status">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </label>
            </div>
          </div>

          <textarea
            value={markdown} onChange={(e) => setMarkdown(e.target.value)}
            rows={16}
            className="eav-input w-full font-mono text-sm leading-snug"
            placeholder="--- title: My finding ---  &#10;# Heading&#10;Body…"
            data-testid="admin-pub-import-textarea"
          />

          {err && (
            <div className="p-3 border border-[#c46a5a]/40 bg-[#c46a5a]/10 text-sm text-[#e9a394] rounded-sm"
                 data-testid="admin-pub-import-error">{err}</div>
          )}

          <details className="text-xs text-[#8a7e6d]">
            <summary className="cursor-pointer hover:text-[#c9bca4]">Supported front-matter keys</summary>
            <div className="mt-2 font-mono-eav text-[11px] leading-relaxed">
              <code>title</code>, <code>slug</code>, <code>subtitle</code>, <code>excerpt</code>,{" "}
              <code>category</code>, <code>industry</code>, <code>tags</code>, <code>featured</code>,{" "}
              <code>status</code>, <code>platforms_tested</code>, <code>finding_summary</code>,{" "}
              <code>business_implication</code>, <code>executive_summary</code>, <code>key_findings</code>,{" "}
              <code>key_takeaway</code>, <code>meta_title</code>, <code>meta_description</code>
            </div>
          </details>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#2a2620]">
          <button onClick={onClose} className="text-sm text-[#8a7e6d] hover:text-[#f4efe6]"
                  data-testid="admin-pub-import-cancel">
            Cancel
          </button>
          <button onClick={onSubmit} disabled={busy || !markdown.trim()}
                  className="btn-gold text-sm disabled:opacity-50"
                  data-testid="admin-pub-import-submit">
            {busy ? "Importing…" : "Import & open editor"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPublications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/publications", { params: { page_size: 100 } });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const onDelete = async (id, title) => {
    const ok = window.confirm(`Delete "${title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/admin/publications/${id}`);
      await fetchData();
    } catch (e) {
      window.alert("Delete failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const filtered = filter
    ? items.filter((it) => it.category === filter)
    : items;

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="admin-publications-page">
      <Helmet><title>Admin · Publications</title></Helmet>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-serif-display text-3xl text-[#f4efe6]">Publications</h1>
            <p className="text-sm text-[#8a7e6d] mt-1">The AI Visibility Index™ CMS</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setImportOpen(true)}
                    data-testid="admin-pub-import-open"
                    className="inline-flex items-center gap-2 px-3 py-2 border border-[#2a2620] text-[#c9bca4] hover:border-[#b8963e] hover:text-[#b8963e] rounded-sm text-sm font-mono-eav tracking-wider uppercase">
              <FileUp className="w-4 h-4" /> Import .md
            </button>
            <button onClick={() => navigate("/admin/publications/new")}
                    data-testid="admin-pub-new"
                    className="btn-gold text-sm">
              <Plus className="w-4 h-4" /> New publication
            </button>
          </div>
        </div>

        {importOpen && (
          <ImportModal
            onClose={() => setImportOpen(false)}
            onCreated={(pub) => {
              setImportOpen(false);
              navigate(`/admin/publications/${pub.id}/edit`);
            }}
          />
        )}

        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-pub-filters">
          <button onClick={() => setFilter("")}
                  className={`px-3 py-1 text-xs rounded-sm font-mono-eav tracking-wider uppercase
                    ${filter === "" ? "bg-[#b8963e] text-[#0c0a08]" : "text-[#c9bca4] border border-[#2a2620] hover:border-[#b8963e]"}`}>
            All ({items.length})
          </button>
          {Object.entries(CATEGORY_LABEL).map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
                    data-testid={`admin-pub-filter-${k}`}
                    className={`px-3 py-1 text-xs rounded-sm font-mono-eav tracking-wider uppercase
                      ${filter === k ? "bg-[#b8963e] text-[#0c0a08]" : "text-[#c9bca4] border border-[#2a2620] hover:border-[#b8963e]"}`}>
              {label} ({items.filter((x) => x.category === k).length})
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[#8a7e6d]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-[#8a7e6d]">No publications yet.</p>
        ) : (
          <div className="border border-[#2a2620] rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0c0a08] border-b border-[#2a2620]">
                <tr className="font-mono-eav text-[10px] tracking-[0.18em] uppercase text-[#8a7e6d]">
                  <th className="text-left px-4 py-3">Title</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Category</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Industry</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">Updated</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-[#2a2620] hover:bg-[#0c0a08]/60" data-testid={`admin-pub-row-${it.slug}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {it.featured && <Star className="w-3 h-3 text-[#b8963e]" />}
                        <span className="text-[#f4efe6]">{it.title}</span>
                      </div>
                      <p className="text-[10px] text-[#5e5448] font-mono-eav mt-0.5">/{it.slug}</p>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-[#c9bca4]">{CATEGORY_LABEL[it.category]}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-[#c9bca4]">{it.industry || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-sm font-mono-eav text-[10px] tracking-wider uppercase
                        ${it.status === "published" ? "bg-[#3e8a4d]/20 text-[#5db368]"
                          : it.status === "scheduled" ? "bg-[#b8963e]/20 text-[#b8963e]"
                          : "bg-[#5e5448]/20 text-[#8a7e6d]"}`}>
                        {STATUS_LABEL[it.status] || it.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[#8a7e6d] text-xs">
                      {it.updated_at ? new Date(it.updated_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        {it.status === "published" && (
                          <Link to={`/research/${it.category}/${it.slug}`} target="_blank"
                                title="View live"
                                className="p-1.5 text-[#8a7e6d] hover:text-[#b8963e]">
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        )}
                        <Link to={`/admin/publications/${it.id}/edit`}
                              data-testid={`admin-pub-edit-${it.slug}`}
                              title="Edit"
                              className="p-1.5 text-[#c9bca4] hover:text-[#b8963e]">
                          <Edit3 className="w-4 h-4" />
                        </Link>
                        <button onClick={() => onDelete(it.id, it.title)}
                                data-testid={`admin-pub-delete-${it.slug}`}
                                title="Delete"
                                className="p-1.5 text-[#c9bca4] hover:text-[#c46a5a]">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
